import Config from '@config';
import { initializeEmployeesApi } from '@gasbi/shared/common/features/employees';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { toLegacyEmployee } from './toLegacyEmployee';
import {
  EmployeeDeactivationPayload,
  LegacyEmployee,
  RbacEmployee,
} from './types';

// Households Billing's employees, the shared RBAC endpoints (GSB-8308, BMS's before: same controller, same shape):
// the list, by-role, accessible/for-organization and by-identity answers are grids, an employee by id is flat.
const employees = initializeEmployeesApi({
  baseUrl: Config.VITE_API_URL,
  baseQuery: axiosBaseQuery,
});

const householdsEmployeesApi = employees.employeesApi.injectEndpoints({
  endpoints: builder => ({
    // For the legacy employee form of @gasbi/shared/web/Organization, which reads the nested shape.
    getLegacyEmployeeById: builder.query<LegacyEmployee, string>({
      query: id => ({ url: `/${id}` }),
      transformResponse: (employee: RbacEmployee) => toLegacyEmployee(employee),
      providesTags: (_, __, id) => [{ type: 'Employee', id }],
    }),
    // The deactivation payload of the shared employee pages (@gasbi/shared/web/RbacOrganization).
    deactivateEmployee: builder.mutation<void, EmployeeDeactivationPayload>({
      query: ({ employeeId, endDate }) => ({
        url: `/${employeeId}/deactivate`,
        method: 'POST',
        data: { employeeId, endDate },
      }),
      invalidatesTags: (_, __, { employeeId }) => [
        'Employees',
        'AllEmployeeRole',
        { type: 'Employee', id: employeeId },
      ],
    }),
  }),
});

export const { useGetLegacyEmployeeByIdQuery, useDeactivateEmployeeMutation } =
  householdsEmployeesApi;

export const {
  employeesApi,
  useGetEmployeesByRoleQuery,
  useGetEmployeeByIdQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeByIdMutation,
  useActivateEmployeeByIdMutation,
  useResetEmployeePasswordByIdMutation,
  useDeactivateEmployeeByIdMutation,
  useReactivateEmployeeByIdMutation,
  useLazyGetEmployeeByEmailQuery,
  useGetEmployeeByIdentityDetailsQuery,
  useActivateEmployeeByIdentityIdMutation,
  useResetPasswordEmployeeByIdentityIdMutation,
  useGetEmployeesInfiniteQuery,
  useGetEmployeesByIdentityInfiniteQuery,
  useGetEmployeesByRoleInfiniteQuery,
  useGetEmployeesAllInfiniteQuery,
  useGetEmployeesAccessibleByOrganizationQuery,
  useGetEmployeeByIdentityIdSelector,
} = employees;
